import React, { useEffect, useReducer, useState } from 'react';

import {
    AccountsContext,
    AuthStateContext,
    BookingContext,
    MockupThemeContext,
    VehicleAssignmentsContext,
} from '../../../../Context';
import { initialStateBookings, BookingsReducer } from '../../../../Context/reducer/bookingsReducer';
import { initialStateSettings, AccountSettingReducer } from '../../../../Context/reducer/accountSettingsReducer';
import {
    initialStateVehicleAssignment,
    VehicleAssignmentReducer,
} from '../../../../Context/reducer/vehicleAssignmentsReducer';
import { initialStateMockupTheme, MockupThemeReducer } from '../../../../Context/reducer/mockupThemeReducer';
import { AuthReducer, initialStateAuth } from '../../../../Context/reducer/authReducer';

import Loading from '../../../../Components/Loading';
import Mockup from '../../../layouts/Mockup';
import DesktopDriverDetails from '../../mockups/DriverDetails';
import MobileDriverDetails from '../../mobileMockups/DriverDetails';
import { applyPortalTextColor, mobileAndTabletCheck, convertQueryStringToObject } from '../../../../utils/helper';
import { getPublicBookingDriverDetailsService } from '../../../../services/bookingsService';

const PublicDriverDetails = (props) => {
    const bookingId = props.match.params.id;
    const { ts } = convertQueryStringToObject(props.location?.search || '');
    const isAuthenticated = Boolean(localStorage.getItem('token'));

    const [bookings, bookingsDispatch] = useReducer(BookingsReducer, initialStateBookings);
    const [tripSettings, AccountsDispatch] = useReducer(AccountSettingReducer, initialStateSettings);
    const [vehicleAssignments, vehicleAssignmentsDispatch] = useReducer(
        VehicleAssignmentReducer,
        initialStateVehicleAssignment
    );
    const [mockupTheme, mockupThemeDispatch] = useReducer(MockupThemeReducer, initialStateMockupTheme);
    const [authUser, authDispatch] = useReducer(AuthReducer, initialStateAuth);

    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const isMobile = mobileAndTabletCheck();

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const response = await getPublicBookingDriverDetailsService(bookingId, ts);
                if (!isMounted) return;
                const data = response?.data || {};
                bookingsDispatch({
                    type: 'FETCH_BOOKING_DETAILS_SUCCESS',
                    details: data.bookingDetails || {},
                    addonAssignments: [],
                    dispatches: [],
                });
                bookingsDispatch({ type: 'BOOKING_VEHICLES_DETAILS_SUCCESS', payload: data.vehicles || [] });
                vehicleAssignmentsDispatch({
                    type: 'FETCH_VEHICLE_ASSIGNMENTS_SUCCESS',
                    payload: data.vehicleAssignments || [],
                });
                applyPortalTextColor(data.tripSettings?.portalTextColor);
                AccountsDispatch({ type: 'GET_SETTINGS_SUCCESS', payload: data.tripSettings || {} });
            } catch (err) {
                console.error('Error loading public driver details', err);
                if (isMounted) setErrorMessage('Unable to load driver details for this booking.');
            } finally {
                if (isMounted) setLoading(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [bookingId, ts]);

    if (loading) return <Loading loading={loading} />;

    if (errorMessage) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="text-center text-muted">{errorMessage}</div>
            </div>
        );
    }

    const Component = isMobile ? MobileDriverDetails : DesktopDriverDetails;

    const page = (
        <BookingContext.Provider value={{ bookings, bookingsDispatch }}>
            <VehicleAssignmentsContext.Provider value={{ vehicleAssignments, vehicleAssignmentsDispatch }}>
                <Component {...props} />
            </VehicleAssignmentsContext.Provider>
        </BookingContext.Provider>
    );

    if (!isAuthenticated) {
        return (
            <MockupThemeContext.Provider value={{ mockupTheme, mockupThemeDispatch }}>
                <AccountsContext.Provider value={{ tripSettings, AccountsDispatch }}>{page}</AccountsContext.Provider>
            </MockupThemeContext.Provider>
        );
    }

    return (
        <AuthStateContext.Provider value={{ authUser, authDispatch }}>
            <MockupThemeContext.Provider value={{ mockupTheme, mockupThemeDispatch }}>
                <AccountsContext.Provider value={{ tripSettings, AccountsDispatch }}>
                    <Mockup mobileAndTabletCheck={isMobile}>{page}</Mockup>
                </AccountsContext.Provider>
            </MockupThemeContext.Provider>
        </AuthStateContext.Provider>
    );
};

export default PublicDriverDetails;
